import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { FocusEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { getLineItemsApi } from 'api/repairOrder';
import { getAdvisorNumberListApi, getTechNumberListApi } from 'api/tickets';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import type { CreateTicketRequestBodyDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { LineItemI } from 'declarations/interfaces/lineItems';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { getOemFamilyIdByRoofTopId } from 'helpers/getOemFamilyByRoofTop';
import { convertNumbersToSelectOptions } from 'helpers/selectOptions';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchAgentsList } from 'hooks/api/useTicketAgentsList';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRoExists } from 'hooks/api/useTicketRoNumber';
import { useCreateMultipleTickets, useCreateTicket } from 'hooks/api/useTickets';
import { useFetchTicketRequestSubcategoriesByCategoryId } from 'hooks/api/useTicketSubcategories';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { NewTicketFormValuesT } from '../declarations';

type UseOpenNewTicketPopupT = {
  requestCategoriesOptions: SelectOptionT[];
  isLoadingRequestCategoriesOptions: boolean;
  ticketRequestSubcategoriesOptions: SelectOptionT[];
  isLoadingTicketRequestSubcategoriesOptions: boolean;
  claimStatusesOptions: SelectOptionT[];
  isLoadingClaimStatusesOptions: boolean;
  ticketPrioritiesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingRoofTopOptions: boolean;
  isLoadingAgentsOptions: boolean;
  roofTopIdsOptions: SelectOptionT[];
  agentIdsOptions: SelectOptionT[];
  isPopupOpened: boolean;
  handleSubmitForm: (data: NewTicketFormValuesT) => void;
  handleAmountBlur: (
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  isCreatingNewTicket: boolean;
  handleCloseNewTicketPopup: NoopT;
  handleRoofTopChange: (roofTopId: string) => void;
  handleAgentChange: (agentId: string) => void;
  handleCategoryChange: (categoryId: number) => void;
  ticketRequestCategories: TicketRequestCategoryI[] | undefined;
  ticketRequestSubcategories: TicketRequestSubcategoryI[] | undefined;
  isRoWarningPopupOpen: boolean;
  advisorNumber: string | undefined;
  checkIfRoExists: (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
  handleLineNumberChange: (lineNumber: string) => void;
  handleROChange: (roNumber: string) => void;
  lineItemsObj: LineItemI[];
  lineNumbers: SelectOptionT[];
  advisorNumbers: SelectOptionT[];
  techNumbers: SelectOptionT[];
  recentRoData: { roofTopId: string | null; roNumber: string | null };
  setAdvisorNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsRoWarningPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLineItemsObj: React.Dispatch<React.SetStateAction<LineItemI[]>>;
  setLineNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setAdvisorNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setTechNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setRecentRoData: React.Dispatch<
    React.SetStateAction<{
      roofTopId: string | null;
      roNumber: string | null;
      agentId: string | null;
    }>
  >;
  setShowLineNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowTechNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAdvisorNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLineNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  showLineNumberDropdown: boolean;
  showTechNumberDropdown: boolean;
  showAdvisorNumberDropdown: boolean;
  setSelectedRoofTopId: React.Dispatch<React.SetStateAction<string | undefined>>;
  handleAdvisorNumberChange: (
    advisorNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
};

const useOpenNewTicketPopup = (): UseOpenNewTicketPopupT => {
  /**
   * Hooks
   */
  const { setNewTicketPopupVisibility, isNewTicketPopupOpened, scheduleCategoryId, roofTopsList } =
    useTickets();
  const [selectedRoofTopId, setSelectedRoofTopId] = useState<string | undefined>(undefined);
  const [, setSelectedAgentId] = useState<string | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const { userProfile, isLoadingUserProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [, setSelectedRoNumber] = useState<string | null>(null);
  const [, setSelectedLineNumber] = useState<string | undefined>(undefined);
  const [isRoWarningPopupOpen, setIsRoWarningPopupOpen] = useState(false);
  const [showLineNumberDropdown, setShowLineNumberDropdown] = useState(false);
  const [showTechNumberDropdown, setShowTechNumberDropdown] = useState(false);
  const [showAdvisorNumberDropdown, setShowAdvisorNumberDropdown] = useState(false);
  const [advisorNumber, setAdvisorNumber] = useState<string | undefined>(undefined);
  const [lineNumbers, setLineNumbers] = useState<SelectOptionT[]>([]);
  const [techNumbers, setTechNumbers] = useState<SelectOptionT[]>([]);
  const [advisorNumbers, setAdvisorNumbers] = useState<SelectOptionT[]>([]);
  const [lineItemsObj, setLineItemsObj] = useState<LineItemI[]>([]);
  const [recentRoData, setRecentRoData] = useState<{
    roofTopId: string | null;
    roNumber: string | null;
    agentId: string | null;
  }>({
    roofTopId: null,
    roNumber: null,
    agentId: null,
  });
  /**
   * Api hooks
   */
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(
    isNewTicketPopupOpened,
    selectedRoofTopId
  );

  const [searchParams] = useSearchParams();
  const effectiveRoofTopId =
    searchParams.get(TicketsRefinementKeysE.RoofTopId) || selectedRoofTopId || '';
  const oemFamilyId = useMemo(
    (): string | undefined => getOemFamilyIdByRoofTopId(effectiveRoofTopId, roofTopsList),
    [effectiveRoofTopId, roofTopsList]
  );

  const ticketRequestSubcategoriesResult = useFetchTicketRequestSubcategoriesByCategoryId({
    isEnabled: isNewTicketPopupOpened && !!selectedRoofTopId && !!selectedCategoryId,
    categoryId: selectedCategoryId,
    oemFamilyId,
  });

  const roofTopId = effectiveRoofTopId;

  const claimStatusesRequestParams = {
    isEnabled: isNewTicketPopupOpened,
    roofTopId: roofTopId,
  };
  const ticketUseFetchAgentsListResult = useFetchAgentsList(isNewTicketPopupOpened, [
    selectedRoofTopId as string,
  ]);
  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);
  const ticketPrioritiesResult = useFetchTicketPriorities(isNewTicketPopupOpened);
  const createTicketMutation = useCreateTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });
  const createMultipleTicketsMutation = useCreateMultipleTickets({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });
  const roQuery = useFetchTicketRoExists(
    recentRoData.roofTopId as string,
    recentRoData.roNumber as string
  );
  /**
   * Variables
   */
  const requestCategoriesOptions = useMemo(
    () =>
      ticketRequestCategoriesResult.data
        ? ticketRequestCategoriesResult.data
            ?.filter(category => category.id !== scheduleCategoryId)
            .map(({ id, categoryName }) => ({
              label: categoryName,
              value: id,
            }))
        : [],
    [ticketRequestCategoriesResult.data, scheduleCategoryId]
  );
  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  const ticketRequestSubcategoriesOptions = useMemo(
    () =>
      ticketRequestSubcategoriesResult.data
        ? ticketRequestSubcategoriesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketRequestSubcategoriesResult.data]
  );
  const roofTopIdsOptions = useMemo(
    () =>
      userProfile
        ? userProfile?.rooftopPermissions
            .map(({ rooftopId, rooftop }) => ({ label: rooftop, value: rooftopId }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [userProfile]
  );

  const agentIdsOptions = useMemo(
    () =>
      ticketUseFetchAgentsListResult.data
        ? ticketUseFetchAgentsListResult.data
            .map(({ userId, displayName }) => ({ label: displayName, value: userId }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [ticketUseFetchAgentsListResult.data]
  );

  /**
   * Handlers
   */
  const resetTicketMutationState = useCallback((): void => {
    if (createTicketMutation.isSuccess || createTicketMutation.isError) {
      createTicketMutation.reset();
    }
  }, [createTicketMutation]);

  const resetMultipleTicketsMutationState = useCallback((): void => {
    if (createMultipleTicketsMutation.isSuccess || createMultipleTicketsMutation.isError) {
      createMultipleTicketsMutation.reset();
    }
  }, [createMultipleTicketsMutation]);

  const handleCloseNewTicketPopup = useCallback((): void => {
    setNewTicketPopupVisibility(false);
    resetTicketMutationState();
    resetMultipleTicketsMutationState();
  }, [resetTicketMutationState, setNewTicketPopupVisibility, resetMultipleTicketsMutationState]);

  const setAmountValue = (value: string, setValue: UseFormSetValue<NewTicketFormValuesT>): void => {
    setValue('amount', value, { shouldValidate: true });
  };
  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<NewTicketFormValuesT>) =>
      (e: FocusEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        const [numbersBeforeDot, numbersAfterDot] = value.split('.');
        const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
        const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

        if (!value) {
          setAmountValue('0.00', setValue);
        } else if (value && hasOneDecimal) {
          setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
        } else if (value && !hasTwoDecimal) {
          setAmountValue(`${numbersBeforeDot}.00`, setValue);
        }
      },
    []
  );
  const handleSubmitForm = useCallback(
    (data: NewTicketFormValuesT) => {
      const onMutationSuccess = (): void => {
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: 'Ticket successfully created',
            snackBarSeverity: 'success',
          })
        );
        handleCloseNewTicketPopup();
      };
      const onMutationError = (error: unknown): void => {
        const errorMsg =
          getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
            snackBarSeverity: 'error',
          })
        );
        handleCloseNewTicketPopup();
      };

      if (data.createMultipleTickets) {
        const tickets: CreateTicketRequestBodyDTO[] = [];
        for (const categoryId of Object.keys(data)) {
          if (categoryId.startsWith('requestCategoryId-')) {
            const resultId = categoryId.split('-').slice(1).join('-');
            const textKey = `text-${resultId}`;

            if (data[categoryId] !== undefined) {
              tickets.push({
                requestCategoryId: data[categoryId],
                roDate: data.roDate ? format(data.roDate, 'yyyy-MM-dd') : '1970-01-01',
                amount: Number(data.amount),
                text: data[textKey],
                roofTopId: data.roofTopId,
                agentId: data.agentId,
                roNumber: data.roNumber,
                lineNumber: data.lineNumber,
                techNumber: data.techNumber,
                advisorNumber: data.advisorNumber,
                vin: data.vin ?? undefined,
                claimStatusId: data.claimStatusId,
                ticketPriorityId: data.ticketPriorityId,
                claimSubmittedDate: data.claimSubmittedDate
                  ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
                  : null,
                requestSubCategoryId: null,
                requestSubCategoryName: null,
              });
            }
          }
        }
        const formData = {
          tickets: tickets,
        };
        createMultipleTicketsMutation.mutate(formData, {
          onSuccess: onMutationSuccess,
          onError: onMutationError,
        });
      } else {
        const parsedData = {
          requestCategoryId: data.requestCategoryId,
          requestSubCategoryId: null,
          requestSubCategoryName: null,
          roDate: data.roDate ? format(data.roDate, 'yyyy-MM-dd') : '1970-01-01',
          amount: Number(data.amount),
          text: data.text,
          roofTopId: data.roofTopId,
          agentId: data.agentId,
          roNumber: data.roNumber,
          lineNumber: data.lineNumber,
          techNumber: data.techNumber,
          advisorNumber: data.advisorNumber,
          vin: data.vin ?? undefined,
          claimStatusId: data.claimStatusId,
          ticketPriorityId: data.ticketPriorityId,
          claimSubmittedDate: data.claimSubmittedDate
            ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
            : null,
          subCategoryId: data.subCategoryId || null,
        };
        createTicketMutation.mutate(parsedData, {
          onSuccess: onMutationSuccess,
          onError: onMutationError,
        });
      }
    },
    [createTicketMutation, createMultipleTicketsMutation, dispatch, handleCloseNewTicketPopup]
  );

  const handleRoofTopChange = (roofTopId: string): void => {
    setSelectedRoofTopId(roofTopId);
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
    }));
  };

  const handleAgentChange = (agentId: string): void => {
    setSelectedAgentId(agentId);
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      agentId: agentId,
    }));
  };

  const handleCategoryChange = (categoryId: number): void => {
    setSelectedCategoryId(categoryId);
  };

  const handleROChange = (roNumber: string | null): void => {
    setSelectedRoNumber(roNumber);
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      roNumber: roNumber,
    }));
  };

  const handleAdvisorNumberChange = (
    advisorNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): void => {
    setAdvisorNumber(advisorNumber);
    setValue('advisorNumber', advisorNumber);
  };

  const handleLineNumberChange = (lineNumber: string): void => {
    setSelectedLineNumber(recentRoData.roNumber ? lineNumber : '');
  };

  const setAdvisorLineNumber = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): Promise<void> => {
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
      roNumber: roNumber,
    }));
    const roResult = roQuery.data;
    setValue('advisorNumber', roResult?.serviceAdvisorNumber ?? advisorNumber);
    setAdvisorNumber(roResult?.serviceAdvisorNumber ?? advisorNumber);
    if (roQuery.isError || (roNumber && roQuery.data === undefined)) {
      setShowLineNumberDropdown(false);
      setSelectedLineNumber(undefined);
      setValue('lineNumber', '');
      setLineItemsObj([]);
      let advisorOptions: SelectOptionT[] = [];
      let techOptions: SelectOptionT[] = [];
      try {
        const advisorNumbers = await getAdvisorNumberListApi(roofTopId);
        advisorOptions = convertNumbersToSelectOptions(advisorNumbers);
      } catch (e) {
        // If fetching advisor numbers fails (e.g., 400 when RoofTopOEM missing), fallback to manual input
        advisorOptions = [];
      }

      try {
        const techNumbers = await getTechNumberListApi(roofTopId);
        techOptions = convertNumbersToSelectOptions(techNumbers);
      } catch (e) {
        // If fetching tech numbers fails (e.g., 400 when RoofTopOEM missing), fallback to manual input
        techOptions = [];
      }

      const hasAdvisorOptions = advisorOptions.length > 0;
      const hasTechOptions = techOptions.length > 0;

      setAdvisorNumbers(advisorOptions);
      setTechNumbers(techOptions);
      setShowAdvisorNumberDropdown(hasAdvisorOptions);
      setShowTechNumberDropdown(hasTechOptions);
    } else {
      const lineItems = getLineItemsApi(roofTopId, roNumber);
      setLineItemsObj(await lineItems);
      setAdvisorNumbers([]);
      setTechNumbers([]);
      setShowAdvisorNumberDropdown(false);
      setShowTechNumberDropdown(false);

      const mappedLineItems = (await lineItems).map(item => {
        return {
          value: item.lineNumber,
          label: item.lineNumber,
        };
      });
      if (mappedLineItems.length > 0) {
        setLineNumbers(mappedLineItems);
        setShowLineNumberDropdown(true);
      } else {
        setShowLineNumberDropdown(false);
      }
    }
  };

  const checkIfRoExists = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): Promise<void> => {
    if (!roofTopId && !roNumber) {
      return;
    }
    if (roofTopId && roNumber && roNumber !== null) {
      setAdvisorLineNumber(roofTopId, roNumber, setValue);
    }
  };

  return {
    requestCategoriesOptions,
    isLoadingRequestCategoriesOptions:
      ticketRequestCategoriesResult.isLoading && !!selectedRoofTopId,
    ticketRequestSubcategoriesOptions,
    isLoadingTicketRequestSubcategoriesOptions: ticketRequestSubcategoriesResult.isLoading,
    isLoadingAgentsOptions: ticketUseFetchAgentsListResult.isLoading && !!selectedRoofTopId,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions: claimStatusesResult.isLoading,
    isLoadingRoofTopOptions: isLoadingUserProfile,
    roofTopIdsOptions,
    agentIdsOptions,
    handleCloseNewTicketPopup,
    isPopupOpened: isNewTicketPopupOpened,
    handleSubmitForm,
    handleAmountBlur,
    isCreatingNewTicket:
      createTicketMutation.isLoading || (createMultipleTicketsMutation.isLoading as boolean),
    handleRoofTopChange,
    handleAgentChange,
    handleCategoryChange,
    ticketRequestCategories: ticketRequestCategoriesResult.data,
    ticketRequestSubcategories: ticketRequestSubcategoriesResult.data,
    advisorNumber,
    checkIfRoExists,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    lineNumbers,
    recentRoData,
    setAdvisorNumber,
    setIsRoWarningPopupOpen,
    setLineItemsObj,
    setLineNumbers,
    setRecentRoData,
    setSelectedLineNumber,
    setShowLineNumberDropdown,
    showLineNumberDropdown,
    isRoWarningPopupOpen,
    setSelectedRoofTopId,
    setShowAdvisorNumberDropdown,
    setShowTechNumberDropdown,
    showAdvisorNumberDropdown,
    showTechNumberDropdown,
    setAdvisorNumbers,
    setTechNumbers,
    techNumbers,
    advisorNumbers,
    handleAdvisorNumberChange,
  };
};

export default useOpenNewTicketPopup;
